/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useReducer } from "react";
import dayjs from "dayjs";
import { When } from "react-if";
import { Button, theme } from "@nordcloud/gnui";
import {
  CloudAccountsSortFields,
  CloudAccountStatus,
  CloudAccountCloudProvider,
  SortOrder,
} from "~/generated/graphql";
import { Mode, Sidebar } from "~/components";
import { FilterSidebarFooter } from "~/components/SidebarWithMultiSelect/FilterSidebarFooter";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableDateRange } from "~/components/SidebarWithMultiSelect/SelectableDateRange";
import { SelectableListWithSearch } from "~/components/SidebarWithMultiSelect/SelectableListWithSearch";
import { Item } from "~/components/SidebarWithMultiSelect/types";
import { dateFormat } from "~/constants";
import { useDisclosure, useQueryState } from "~/hooks";
import {
  getFilterSidebarState,
  getLivePreviewFilterSidebarFooterState,
  hasFilterChanges,
  isNotEmpty,
  isNotNil,
} from "~/tools";
import { accountsExcludeFilterMap } from "~/tools/filterStateMaps";
import { useGetCloudAccounts } from "~/views/accounts/hooks/useGetCloudAccounts/useGetCloudAccounts";
import { AccountQuery } from "../types/AccountQuery";
import { getAccountStatusMessage } from "../utils";
import { prepareCloudAccountsSearchFilter } from "./accountFilterBuilders";

type State = {
  startDate?: string;
  endDate?: string;
  cloudProviders?: string[];
  excludeCloudProviders?: boolean;
  cloudAccountStatuses?: string[];
  excludeCloudAccountStatuses?: boolean;
};
type AccountQueryState = AccountQuery & {
  page?: string | number;
  limit?: string | number;
};

function buildState(queryState: AccountQuery) {
  return {
    startDate: queryState.startDateUTC
      ? dayjs(queryState.startDateUTC).format(dateFormat.shortDate)
      : undefined,
    endDate: queryState.endDateUTC
      ? dayjs(queryState.endDateUTC).format(dateFormat.shortDate)
      : undefined,
    cloudProviders: queryState.cloudProviders,
    excludeCloudProviders: queryState.excludeCloudProviders,
    cloudAccountStatuses: queryState.cloudAccountStatuses,
    excludeCloudAccountStatuses: queryState.excludeCloudAccountStatuses,
  };
}

function buildQueryStateFromSidebarState(
  queryState: AccountQueryState,
  state: State
): AccountQueryState {
  return {
    ...queryState,
    startDateUTC: state.startDate
      ? dayjs(state.startDate).format(dateFormat.fullDate)
      : undefined,
    endDateUTC: state.endDate
      ? dayjs(state.endDate).endOf("day").format(dateFormat.fullDate)
      : undefined,
    cloudProviders: state.cloudProviders,
    excludeCloudProviders: state.excludeCloudProviders,
    cloudAccountStatuses: state.cloudAccountStatuses,
    excludeCloudAccountStatuses: state.excludeCloudAccountStatuses,
  };
}

export function CloudAccountsFiltersSidebar({
  resultsCount = 0,
}: {
  resultsCount?: number;
}) {
  const { isOpen, open, close } = useDisclosure();
  const { queryState, updateQueryState } = useQueryState<AccountQuery>();

  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => ({ ...data, ...partialData }),
    buildState(queryState)
  );

  useEffect(() => {
    updateState(buildState(queryState));
  }, [queryState, isOpen]);

  const appliedState = buildState(queryState);
  const hasPendingChanges = hasFilterChanges(
    state,
    appliedState,
    accountsExcludeFilterMap
  );
  const draftQueryState = useMemo(
    () => buildQueryStateFromSidebarState(queryState, state),
    [queryState, state]
  );
  const phrase = draftQueryState.searchPhrase?.trim();

  const { count: previewCount, loading: previewLoading } = useGetCloudAccounts({
    page: String(draftQueryState.page ?? "1"),
    limit: String(draftQueryState.limit ?? "20"),
    sort: {
      field: draftQueryState.field ?? CloudAccountsSortFields.Name,
      order: draftQueryState.order ?? SortOrder.Asc,
    },
    filter: prepareCloudAccountsSearchFilter(draftQueryState, phrase),
    skip: !isOpen || !hasPendingChanges,
  });
  const previewResultsCount = previewLoading ? undefined : previewCount;
  const previewFooterState = getLivePreviewFilterSidebarFooterState({
    hasPendingChanges,
    isOpen,
    previewLoading,
    previewResultsCount,
    appliedResultsCount: resultsCount,
    entityLabel: "accounts",
  });

  const applyFilters = () => {
    const nextQueryState = buildQueryStateFromSidebarState(queryState, state);

    updateQueryState({
      ...nextQueryState,
      limit: String(nextQueryState.limit ?? queryState.limit ?? "20"),
      page: "1",
    });
    close();
  };

  const clearAllFilters = () => {
    updateState({
      startDate: undefined,
      endDate: undefined,
      cloudProviders: undefined,
      excludeCloudProviders: undefined,
      cloudAccountStatuses: undefined,
      excludeCloudAccountStatuses: undefined,
    });
    updateQueryState({
      ...queryState,
      startDateUTC: undefined,
      endDateUTC: undefined,
      cloudProviders: undefined,
      excludeCloudProviders: undefined,
      cloudAccountStatuses: undefined,
      excludeCloudAccountStatuses: undefined,
      page: "1",
    });
    close();
  };

  const clearDateAddedFilter = () => {
    updateState({ startDate: undefined, endDate: undefined });
  };

  const clearCloudProvidersFilter = () => {
    updateState({ cloudProviders: undefined, excludeCloudProviders: false });
  };

  const clearCloudAccountStatusesFilter = () => {
    updateState({
      cloudAccountStatuses: undefined,
      excludeCloudAccountStatuses: false,
    });
  };
  const filterSidebarState = getFilterSidebarState({
    currentState: state,
    appliedState,
    resultsCount: previewFooterState.effectiveResultsCount,
    entityLabel: "accounts",
    excludeFilterMap: accountsExcludeFilterMap,
  });
  const helperText =
    previewFooterState.helperTextOverride ?? filterSidebarState.helperText;

  const cloudResourceProviderCheckboxes: Item[] = [
    {
      value: CloudAccountCloudProvider.Aws,
      label: CloudAccountCloudProvider.Aws,
    },
    {
      value: CloudAccountCloudProvider.Azure,
      label: CloudAccountCloudProvider.Azure,
    },
    {
      value: CloudAccountCloudProvider.Gc,
      label: CloudAccountCloudProvider.Gc,
    },
  ];

  const cloudAccountStatuses: Item[] = [
    {
      value: CloudAccountStatus.AccessError,
      label: getAccountStatusMessage(CloudAccountStatus.AccessError),
      subLabel:
        "Invalid, expired credentials or all specified regions are not accessible",
    },
    {
      value: CloudAccountStatus.AccessMissing,
      label: getAccountStatusMessage(CloudAccountStatus.AccessMissing),
      subLabel: "Credentials to access the account are missing",
    },
    {
      value: CloudAccountStatus.AccessOk,
      label: getAccountStatusMessage(CloudAccountStatus.AccessOk),
      subLabel: "Account is connected, and everything is working as expected",
    },
    {
      value: CloudAccountStatus.InProgress,
      label: getAccountStatusMessage(CloudAccountStatus.InProgress),
      subLabel: "Scanning process is in progress",
    },
    {
      value: CloudAccountStatus.RegionsUnavailable,
      label: getAccountStatusMessage(CloudAccountStatus.RegionsUnavailable),
      subLabel: "Some of specified regions are not accessible",
    },
  ];

  return (
    <>
      <Button
        mr={theme.spacing.spacing04}
        severity="low"
        icon="filter"
        onClick={open}
      >
        Filters
      </Button>
      <When condition={isOpen}>
        <Sidebar
          title="Filter Results"
          isOpen={isOpen}
          onClick={close}
          footer={
            <FilterSidebarFooter
              helperText={helperText}
              isApplyDisabled={filterSidebarState.isApplyDisabled}
              isClearDisabled={filterSidebarState.isClearDisabled}
              onApply={applyFilters}
              onClear={clearAllFilters}
            />
          }
        >
          <FilterWrapper
            name="Date Added"
            isSelected={!!state?.startDate && !!state?.endDate}
            selectedOptions={["startDate", "endDate"]}
            onClear={clearDateAddedFilter}
          >
            <SelectableDateRange
              selectedRange={{
                startDate: state.startDate,
                endDate: state.endDate,
              }}
              onRangeSelect={(range) => updateState(range)}
              label="Time range when account was added"
              setDefaultEndDateToToday
            />
          </FilterWrapper>
          <FilterWrapper
            name="Cloud Provider"
            isSelected={
              isNotNil(state.cloudProviders) && isNotEmpty(state.cloudProviders)
            }
            selectedOptions={state.cloudProviders ?? []}
            onClear={clearCloudProvidersFilter}
          >
            <SelectableListWithSearch
              allowModeChange
              selectedOptions={state.cloudProviders ?? []}
              selectableItems={cloudResourceProviderCheckboxes}
              isExcludeSelected={state.excludeCloudProviders}
              onMultiSelect={(selections) => {
                updateState({ cloudProviders: selections });
              }}
              onModeChange={(mode) => {
                updateState({ excludeCloudProviders: mode === Mode.EXCLUDES });
              }}
            />
          </FilterWrapper>
          <FilterWrapper
            name="Account Status"
            isSelected={
              isNotNil(state.cloudAccountStatuses) &&
              isNotEmpty(state.cloudAccountStatuses)
            }
            selectedOptions={state.cloudAccountStatuses ?? []}
            onClear={clearCloudAccountStatusesFilter}
          >
            <SelectableListWithSearch
              allowModeChange
              selectedOptions={state.cloudAccountStatuses ?? []}
              selectableItems={cloudAccountStatuses}
              isExcludeSelected={state.excludeCloudAccountStatuses}
              onMultiSelect={(selections) => {
                updateState({ cloudAccountStatuses: selections });
              }}
              onModeChange={(mode) => {
                updateState({
                  excludeCloudAccountStatuses: mode === Mode.EXCLUDES,
                });
              }}
            />
          </FilterWrapper>
        </Sidebar>
      </When>
    </>
  );
}
